import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { forkJoin, Subscription } from 'rxjs';
import { NewReportTemplateInfo, ReportTemplate, ReportTemplateUpdate } from 'src/app/_objects/report-template';
import { ReportTemplateService } from 'src/app/_services/report-template.service';
import { EMPTY_TEMPLATE } from 'src/app/app.constants';
import { REPORT_TEMPLATE___APPLICATION_TYPE, StorageService } from '../../_services/storage.service';
import { SpinnerService } from '@osc-utils/services/spinner.service';

@Component({
    selector: 'app-add-new-template-dialog',
    templateUrl: './add-new-template-dialog.component.html',
    styleUrls: ['./add-new-template-dialog.component.scss'],
    standalone: false
})
export class AddNewTemplateDialogComponent implements OnInit, OnDestroy {

  public addTemplateForm: UntypedFormGroup;
  public reportTypes: any[] = [];
  public equipmentTypes: any[] = [];
  private existingTemplate: ReportTemplate;
  private reportTypeChangeSubscription: Subscription;
  private existingTemplateId: string = null;
  public isVisol: boolean;
  public spinnerID = 'add-dialog-spinner';
  isTrans: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    public dialogRef: MatDialogRef<AddNewTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public reportTemplateService: ReportTemplateService,
    public spinnerService: SpinnerService,
    private storageService: StorageService
  ) {
    this.isVisol = false;
  }

  public ngOnInit() {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    const appType = this.storageService.fetchApplicationType(REPORT_TEMPLATE___APPLICATION_TYPE);
    if (appType) {
      this.isVisol = appType.IsVisol;
    }

    this.initForm();
    const requests = [
      this.reportTemplateService.GetReportTypes(),
      this.reportTemplateService.GetEquipmentTypes()
    ];
    if (this.data.action === 'clone' || this.data.action === 'edit') {
      requests.push(this.reportTemplateService.getReportTemplate(this.data.template.id, this.isVisol));
    }
    forkJoin(requests).subscribe((res) => {
      this.reportTypes = res[0].map((el) => ({ value: el.code, label: el.description }));
      this.reportTypes = this.reportTypes.filter(x => x.value !== 'Unknown'); // Removing Unknown type from list
      this.equipmentTypes = res[1].map((el) => ({ value: el.id, label: el.name }));
      if (this.data.action === 'clone' || this.data.action === 'edit') {
        this.existingTemplate = res[2];
        this.existingTemplateId = this.existingTemplate.id;
        if (!this.isVisol) {
          this.addTemplateForm.get('reportType').setValue(this.existingTemplate.reportType);
          this.addTemplateForm.get('equipmentType').setValue(this.existingTemplate.equipmentTypes);
        }
        if (this.data.action === 'edit') {
          const equipmentTypeInTemplate = this.existingTemplate.equipmentType?.length > 0 ? (this.existingTemplate.equipmentType as string).split(',') : !this.existingTemplate.equipmentType ? [''] : [this.existingTemplate.equipmentType];
          const filteredEquipmentTypes = this.equipmentTypes.filter(x => equipmentTypeInTemplate.includes(x.label)).map(x => x.value);
          this.addTemplateForm.get('templateName').setValue(this.existingTemplate.name);
          this.addTemplateForm.get('equipmentType').setValue(filteredEquipmentTypes);
        }
      }
    }, () => {
      this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      this.dialogRef.close(false);
    },
      () => {
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      });
  }

  public initForm(): void {
    if (this.isVisol) {
      this.addTemplateForm = this.formBuilder.group({
        templateName: new UntypedFormControl('', Validators.required)
      });
      // this.reportTypeChangeSubscription
    } else {
      this.addTemplateForm = this.formBuilder.group({
        templateName: new UntypedFormControl('', Validators.required),
        reportType: new UntypedFormControl('', Validators.required),
        equipmentType: new UntypedFormControl('')
      });

      this.reportTypeChangeSubscription = this.addTemplateForm.controls.reportType.valueChanges.subscribe((newVal) => {
        if (newVal === 'VTInspection') {
          this.addTemplateForm.get('equipmentType').setValidators([Validators.required]);
        } else {
          this.addTemplateForm.get('equipmentType').clearValidators();
          this.addTemplateForm.get('equipmentType').setValue([]);
        }
      });
    }
  }

  public closeModal(): void {
    this.dialogRef.close(false);
  }

  public createNewTemplate(): void {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    let newreportTemplate: NewReportTemplateInfo;

    if (this.isVisol) {
      newreportTemplate = {
        templateContent: this.data.action === 'clone' ? JSON.parse(this.existingTemplate.reportTemplateContent) : EMPTY_TEMPLATE,
        reportType: null
      };
    } else {
      newreportTemplate = {
        reportType: this.addTemplateForm.get('reportType').value,
        equipmentTypes: this.addTemplateForm.get('equipmentType').value === undefined
          || this.addTemplateForm.get('equipmentType').value === ''
          ? ''
          : this.addTemplateForm.get('equipmentType').value.join(','),
        templateContent: this.data.action === 'clone' ? JSON.parse(this.existingTemplate.reportTemplateContent) : EMPTY_TEMPLATE
      };
    }

    this.reportTemplateService.createTemplate(this.addTemplateForm.get('templateName').value,
      this.isVisol, newreportTemplate, this.existingTemplateId).subscribe(() => {
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
        this.dialogRef.close(true);
      }, (err) => {
        this.spinnerService.hide(this.spinnerService.mainSpinnerId);
        this.dialogRef.close(false);
      });
  }

  public updateTemplate(): void {
    this.spinnerService.show(this.spinnerService.mainSpinnerId);
    const reportTemplateUpdate = new ReportTemplateUpdate();
    reportTemplateUpdate.id = this.existingTemplate.id;
    reportTemplateUpdate.name = this.addTemplateForm.get('templateName').value
    reportTemplateUpdate.application = this.existingTemplate.application;
    reportTemplateUpdate.createdByTenantId = this.existingTemplate.id;
    reportTemplateUpdate.createdByUserId = this.existingTemplate.id;
    reportTemplateUpdate.createdDateUtc = new Date();
    reportTemplateUpdate.lastUpdatedDateUtc = new Date();
    reportTemplateUpdate.reportTemplateContent = this.existingTemplate.reportTemplateContent;
    reportTemplateUpdate.subjectModelIds = this.existingTemplate.subjectModelIds;
    reportTemplateUpdate.reportType = this.addTemplateForm.get('reportType').value;
    reportTemplateUpdate.equipmentType =
      [... new Set(this.equipmentTypes.filter(x => this.addTemplateForm.get('equipmentType').value.includes(x.value)).map(x => x.label))].toString();
    reportTemplateUpdate.etag = this.existingTemplate.etag;
    reportTemplateUpdate.isValid = this.existingTemplate.isValid;
    reportTemplateUpdate.reportTitleFormat = this.existingTemplate.reportTitleFormat;
    reportTemplateUpdate.reportTitleInputMode = this.existingTemplate.reportTitleInputMode;
    reportTemplateUpdate.schemaVersion = this.existingTemplate.schemaVersion;
    this.reportTemplateService.updateReportTemplate(reportTemplateUpdate, this.isVisol).subscribe((res) => {
      this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      this.dialogRef.close(true);
    }, (error) => {
      this.spinnerService.hide(this.spinnerService.mainSpinnerId);
      console.error(error);
      this.dialogRef.close(false);
    })
  }

  public ngOnDestroy(): void {
    if (this.reportTypeChangeSubscription) {
      this.reportTypeChangeSubscription.unsubscribe();
    }
  }
}
